/**
 * MCP Executor
 *
 * Executes MCP tools using the app's Zustand stores.
 * This is the main entry point for tool execution.
 */

import type { MCPToolResult, MCPToolCall } from './types';
import {
  allHandlers,
  isWriteTool,
  isSimulationTool,
  isAsyncSimulationTool,
  getSimulationHandler,
  getAsyncSimulationHandler,
  type HandlerContext,
  type WriteHandlerContext,
  type SimulationHandlerContext,
} from './handlers';
import type { IDFDocument, IDFObject, IDFField } from '../parser';
import type { IDDSchema } from '../idd';
import type { ConsoleLine, SimulationPhase, TimeEstimate } from '../simulation/progress';
import type { OutputVariable, TimeSeries } from '../simulation/parsers/types';
import type { SimulationOutput, SimulationStatus } from '../simulation/simulationStore';

/**
 * Store access interface for IDF operations
 */
export interface MCPStoreAccess {
  // IDF Store
  getDocument: () => IDFDocument | null;
  getObjectById: (id: string) => IDFObject | undefined;
  updateField: (objectId: string, fieldIndex: number, field: Partial<IDFField>) => void;
  updateObject: (objectId: string, updates: Partial<IDFObject>) => void;
  addObject: (obj: Omit<IDFObject, 'id'>) => string;
  removeObject: (objectId: string) => void;
  duplicateObject: (objectId: string) => string | null;

  // IDD Store
  getSchema: () => IDDSchema | null;
}

/**
 * Simulation store access interface
 */
export interface SimulationStoreAccess {
  // Status
  getStatus: () => SimulationStatus;
  getPhase: () => SimulationPhase;
  getProgress: () => number;
  getProgressMessage: () => string;
  getError: () => string | null;
  getTimeEstimate: () => TimeEstimate;

  // Console
  getConsoleLines: () => ConsoleLine[];

  // Results
  getOutput: () => SimulationOutput | null;
  getAvailableVariables: () => OutputVariable[];
  getTimeSeries: (variableId: number) => TimeSeries | undefined;

  // WASM status
  isWasmLoaded: () => boolean;
  isMockMode: () => boolean;

  // Control
  startSimulation: () => Promise<void>;
  cancelSimulation: () => void;
  forceCancel: () => void;
  setInput: (input: { idf: string; idfName: string; epw?: string; epwName?: string }) => void;
  loadWasm: () => Promise<void>;

  // Input status
  hasInput: () => boolean;

  // IDF content access (for prepare_simulation)
  getIdfContent: () => string | null;
  getIdfName: () => string | null;
  getEpwContent: () => string | null;
  getEpwName: () => string | null;
}

/**
 * Combined store access for all tools
 */
export interface MCPFullStoreAccess extends MCPStoreAccess {
  simulation?: SimulationStoreAccess;
}

/**
 * Execute a single MCP tool call
 */
export function executeTool(
  call: MCPToolCall,
  stores: MCPFullStoreAccess
): MCPToolResult {
  // Check if this is a simulation tool
  if (isSimulationTool(call.name)) {
    const simHandler = getSimulationHandler(call.name);
    if (!simHandler) {
      return {
        success: false,
        error: `Unknown simulation tool: ${call.name}`,
        isError: true,
      };
    }

    if (!stores.simulation) {
      return {
        success: false,
        error: 'Simulation store not available',
        isError: true,
      };
    }

    try {
      const simContext: SimulationHandlerContext = {
        getStatus: stores.simulation.getStatus,
        getPhase: stores.simulation.getPhase,
        getProgress: stores.simulation.getProgress,
        getProgressMessage: stores.simulation.getProgressMessage,
        getError: stores.simulation.getError,
        getTimeEstimate: stores.simulation.getTimeEstimate,
        getConsoleLines: stores.simulation.getConsoleLines,
        getOutput: stores.simulation.getOutput,
        getAvailableVariables: stores.simulation.getAvailableVariables,
        getTimeSeries: stores.simulation.getTimeSeries,
        isWasmLoaded: stores.simulation.isWasmLoaded,
        isMockMode: stores.simulation.isMockMode,
        startSimulation: stores.simulation.startSimulation,
        cancelSimulation: stores.simulation.cancelSimulation,
        forceCancel: stores.simulation.forceCancel,
        setInput: stores.simulation.setInput,
        loadWasm: stores.simulation.loadWasm,
        hasInput: stores.simulation.hasInput,
        getIdfContent: stores.simulation.getIdfContent,
        getIdfName: stores.simulation.getIdfName,
        getEpwContent: stores.simulation.getEpwContent,
        getEpwName: stores.simulation.getEpwName,
      };
      return simHandler(call.arguments, simContext);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error executing simulation tool',
        isError: true,
      };
    }
  }

  // Handle IDF model tools
  const handler = allHandlers[call.name];

  if (!handler) {
    return {
      success: false,
      error: `Unknown tool: ${call.name}`,
      isError: true,
    };
  }

  try {
    // Build context based on tool type
    const baseContext: HandlerContext = {
      document: stores.getDocument(),
      schema: stores.getSchema(),
      getObjectById: stores.getObjectById,
    };

    // Add write capabilities for write tools
    if (isWriteTool(call.name)) {
      const writeContext: WriteHandlerContext = {
        ...baseContext,
        updateField: stores.updateField,
        updateObject: stores.updateObject,
        addObject: stores.addObject,
        removeObject: stores.removeObject,
        duplicateObject: stores.duplicateObject,
      };
      return handler(call.arguments, writeContext);
    }

    return handler(call.arguments, baseContext);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error executing tool',
      isError: true,
    };
  }
}

/**
 * Execute an async simulation tool call
 */
export async function executeToolAsync(
  call: MCPToolCall,
  stores: MCPFullStoreAccess
): Promise<MCPToolResult> {
  // Check if this is an async simulation tool
  if (isAsyncSimulationTool(call.name)) {
    const asyncSimHandler = getAsyncSimulationHandler(call.name);
    if (!asyncSimHandler) {
      return {
        success: false,
        error: `Unknown async simulation tool: ${call.name}`,
        isError: true,
      };
    }

    if (!stores.simulation) {
      return {
        success: false,
        error: 'Simulation store not available',
        isError: true,
      };
    }

    try {
      const simContext: SimulationHandlerContext = {
        getStatus: stores.simulation.getStatus,
        getPhase: stores.simulation.getPhase,
        getProgress: stores.simulation.getProgress,
        getProgressMessage: stores.simulation.getProgressMessage,
        getError: stores.simulation.getError,
        getTimeEstimate: stores.simulation.getTimeEstimate,
        getConsoleLines: stores.simulation.getConsoleLines,
        getOutput: stores.simulation.getOutput,
        getAvailableVariables: stores.simulation.getAvailableVariables,
        getTimeSeries: stores.simulation.getTimeSeries,
        isWasmLoaded: stores.simulation.isWasmLoaded,
        isMockMode: stores.simulation.isMockMode,
        startSimulation: stores.simulation.startSimulation,
        cancelSimulation: stores.simulation.cancelSimulation,
        forceCancel: stores.simulation.forceCancel,
        setInput: stores.simulation.setInput,
        loadWasm: stores.simulation.loadWasm,
        hasInput: stores.simulation.hasInput,
        getIdfContent: stores.simulation.getIdfContent,
        getIdfName: stores.simulation.getIdfName,
        getEpwContent: stores.simulation.getEpwContent,
        getEpwName: stores.simulation.getEpwName,
      };
      return await asyncSimHandler(call.arguments, simContext);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error executing async simulation tool',
        isError: true,
      };
    }
  }

  // For non-async tools, use the sync executor
  return executeTool(call, stores);
}

/**
 * Execute multiple tool calls
 */
export function executeTools(
  calls: MCPToolCall[],
  stores: MCPFullStoreAccess
): MCPToolResult[] {
  return calls.map((call) => executeTool(call, stores));
}

/**
 * Create an MCP executor bound to store accessors
 */
export function createMCPExecutor(stores: MCPFullStoreAccess) {
  return {
    execute: (call: MCPToolCall) => executeTool(call, stores),
    executeAsync: (call: MCPToolCall) => executeToolAsync(call, stores),
    executeMany: (calls: MCPToolCall[]) => executeTools(calls, stores),
    isWriteTool,
    isSimulationTool,
    isAsyncSimulationTool,
  };
}

/**
 * Parse tool calls from Claude's response
 */
export function parseToolCalls(
  response: {
    content: Array<{
      type: string;
      name?: string;
      input?: Record<string, unknown>;
    }>;
  }
): MCPToolCall[] {
  return response.content
    .filter((block): block is { type: 'tool_use'; name: string; input: Record<string, unknown> } =>
      block.type === 'tool_use' && typeof block.name === 'string' && block.input !== undefined
    )
    .map((block) => ({
      name: block.name,
      arguments: block.input,
    }));
}

/**
 * Format tool results for Claude's API
 */
export function formatToolResults(
  results: Array<{ toolName: string; result: MCPToolResult }>
): Array<{
  type: 'tool_result';
  tool_use_id: string;
  content: string;
  is_error?: boolean;
}> {
  return results.map(({ toolName, result }, index) => {
    const base = {
      type: 'tool_result' as const,
      tool_use_id: `tool_${String(index)}_${toolName}`,
      content: JSON.stringify(result.content || { error: result.error }),
    };
    // Only include is_error if it's true (exactOptionalPropertyTypes)
    if (result.isError) {
      return { ...base, is_error: true };
    }
    return base;
  });
}
